import { Button, Card, CardContent, Input, Label, Modal, Spinner } from '@crm/ui';
import { Check, Plus, Trash2, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components';
import {
  useBannerTemplates,
  useCreateBannerTemplate,
  useDeleteBannerTemplate,
  useUpdateBannerTemplate,
} from '../api';
import { AddPhotoModal } from './photo-slots';

const TITULO_MAX = 140;
const CHAMADA_MAX = 80;

interface SlideProps {
  numero: number;
  id: string;
  image: string;
  title: string;
  subtitle: string;
  onUpdate: (input: { title?: string; subtitle?: string }) => void;
  onRemove: () => void;
}

/**
 * Um slide e a unidade inteira: numero, banner (foto), titulo e chamada,
 * nessa ordem, um embaixo do outro - separado do proximo so por uma linha,
 * todos no mesmo box. A foto e zoom ao clicar, excluir so pela modal - trocar
 * e so excluir e adicionar de novo.
 */
function Slide({ numero, id, image, title, subtitle, onUpdate, onRemove }: SlideProps) {
  const [tituloLocal, setTituloLocal] = useState(title);
  const [legendaLocal, setLegendaLocal] = useState(subtitle);
  const [ampliado, setAmpliado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="space-y-3 py-6 first:pt-0 last:pb-0">
      <p className="text-sm font-semibold text-foreground">{numero}º Slide</p>

      <button
        type="button"
        onClick={() => setAmpliado(true)}
        aria-label={`Ver banner ${numero} ampliado`}
        className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-border"
      >
        <img src={image} alt="" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/30 group-hover:text-white">
          <ZoomIn className="h-5 w-5" />
        </span>
      </button>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <Label htmlFor={`banner-titulo-${id}`}>Título</Label>
            <span className="text-xs text-muted-foreground">
              {tituloLocal.length}/{TITULO_MAX}
            </span>
          </div>
          <Input
            id={`banner-titulo-${id}`}
            maxLength={TITULO_MAX}
            value={tituloLocal}
            onChange={(event) => setTituloLocal(event.target.value)}
            onBlur={() => {
              if (tituloLocal !== title) onUpdate({ title: tituloLocal });
            }}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <Label htmlFor={`banner-legenda-${id}`}>Chamada</Label>
            <span className="text-xs text-muted-foreground">
              {legendaLocal.length}/{CHAMADA_MAX}
            </span>
          </div>
          <Input
            id={`banner-legenda-${id}`}
            maxLength={CHAMADA_MAX}
            value={legendaLocal}
            onChange={(event) => setLegendaLocal(event.target.value)}
            onBlur={() => {
              if (legendaLocal !== subtitle) onUpdate({ subtitle: legendaLocal });
            }}
          />
        </div>
      </div>

      {ampliado ? (
        <Modal open onClose={() => setAmpliado(false)} title={`Banner ${numero}`} size="medium">
          <img src={image} alt="" className="w-full rounded-lg object-cover" />
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => setConfirmando(true)}>
              <Trash2 className="h-4 w-4" />
              Excluir banner
            </Button>
          </div>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmando}
        title="Excluir banner"
        message="Este banner sai da lista - da pra adicionar de volta depois."
        confirmLabel="Excluir"
        onClose={() => setConfirmando(false)}
        onConfirm={() => {
          setConfirmando(false);
          setAmpliado(false);
          onRemove();
        }}
      />
    </div>
  );
}

/**
 * Os banners prontos do slide da pagina do salao - o assistente do salao
 * preenche o carrossel com eles, e a pessoa escolhe o estilo depois. Cada
 * slide e uma unidade so (banner + titulo + chamada); pra trocar a foto,
 * exclui e adiciona de novo, igual as fotos de categoria/servico.
 */
export function BannerTemplatesManager() {
  const banners = useBannerTemplates();
  const createBanner = useCreateBannerTemplate();
  const updateBanner = useUpdateBannerTemplate();
  const deleteBanner = useDeleteBannerTemplate();
  const [adicionando, setAdicionando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const lista = banners.data?.data ?? [];

  // Cada campo ja salva sozinho ao perder o foco - este botao so garante que
  // o campo em edicao nesse instante nao fique pendurado sem confirmar.
  function salvarTudo() {
    (document.activeElement as HTMLElement | null)?.blur();
    setSalvo(true);
    setTimeout(() => setSalvo(false), 2000);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Os banners prontos que o assistente de configuracoes usa para montar o slide da pagina do
        salao.
      </p>

      {banners.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : (
        <Card variant="flush">
          <CardContent className="divide-y divide-border py-0">
            {lista.map((banner, indice) => (
              <Slide
                key={banner.id}
                numero={indice + 1}
                id={banner.id}
                image={banner.image}
                title={banner.title}
                subtitle={banner.subtitle}
                onUpdate={(input) => updateBanner.mutate({ id: banner.id, input })}
                onRemove={() => deleteBanner.mutate(banner.id)}
              />
            ))}

            <div className="flex flex-wrap gap-2 py-6 first:pt-0 last:pb-0">
              <Button variant="outline" onClick={() => setAdicionando(true)}>
                <Plus className="h-4 w-4" />
                Adicionar slide
              </Button>
              <Button onClick={salvarTudo}>
                <Check className="h-4 w-4" />
                {salvo ? 'Salvo!' : 'Salvar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {adicionando ? (
        <AddPhotoModal
          title="Adicionar banner"
          onClose={() => setAdicionando(false)}
          onAdd={(foto) => {
            createBanner.mutate({ image: foto, title: '', subtitle: '' });
            setAdicionando(false);
          }}
        />
      ) : null}
    </div>
  );
}
